import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ArrowRight,
  Star as StarIcon,
  Package,
  Globe,
  TrendingUp,
  ChevronRight,
  Bell,
  BadgePercent,
  Coins,
  Sparkles
} from 'lucide-react';
import { ref, get, query, orderByChild, equalTo, limitToLast } from 'firebase/database';

import {
  fetchProducts,
  fetchCarouselImages,
  fetchTrendingProducts,
  selectTrendingProducts,
  selectTrendingLoading,
  selectTrendingWindowDays,
  setSearchQuery,
  fetchBannerHtml,
  fetchStoreDiscounts // Add this
} from '../store/slices/productsSlice';
import { fetchCoupons } from '../store/slices/cartSlice';
import ProductCard from '../components/product/ProductCard';
import CouponCard from '../components/store/CouponCard';
import { PageLoader } from '../components/common/LoadingSpinner';
import BannerCarousel from '../components/home/BannerCarousel';
import SeasonalCampaignHero from '../components/home/SeasonalCampaignHero';
import PopupModal from '../components/common/PopupModal';
import PointsTopUpModal from '../components/modals/PointsTopUpModal';
import { sanitizeHtmlContent } from '../utils/contentSecurity';
import { isCouponVisibleToUser } from '../utils/helpers';
import { fetchRecentNotifications, getNotificationRoute, markNotificationRead } from '../services/notificationService';
import { database } from '../services/firebase';

// New Quick-Access Category Item
const QuickCategoryItem = ({ icon: Icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    className="group min-w-0 rounded-2xl px-1.5 py-2 transition-colors hover:bg-white/70 dark:hover:bg-white/[0.06]"
  >
    <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-white/50 ${color} shadow-lg shadow-gray-900/10 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:scale-105 sm:h-14 sm:w-14 dark:border-white/10 dark:shadow-black/25`}>
      <Icon className="h-5 w-5 text-white sm:h-6 sm:w-6" />
    </div>
    <span className="mt-2 block truncate text-center text-[11px] font-black leading-tight text-gray-700 dark:text-gray-300 sm:text-xs">
      {label}
    </span>
  </button>
);

// Swimlane Component (Horizontal Scroll)
const ProductSwimlane = ({ id, title, subtitle, products, onViewAll, loading, showTopBorder = true }) => {
  const { t } = useTranslation();
  const scrollRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [products]);
  if (loading) return (
    <section id={id} className={`${showTopBorder ? 'border-t border-gray-200/70 dark:border-white/10' : ''} py-4 md:py-5`}>
      <div className="px-3 md:px-0">
        <div className="mb-4 h-6 w-48 animate-pulse rounded-full bg-gray-200 dark:bg-white/10" />
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3].map(i => <div key={i} className="h-48 w-[132px] shrink-0 animate-pulse rounded-3xl bg-white/70 ring-1 ring-gray-200/70 dark:bg-white/[0.06] dark:ring-white/10 md:w-[200px]" />)}
        </div>
      </div>
    </section>
  );

  if (!products || products.length === 0) return null;

  return (
    <section id={id} className={`${showTopBorder ? 'border-t border-gray-200/70 dark:border-white/10' : ''} py-4 md:py-5`}>
      <div className="w-full">
        {(title || subtitle || onViewAll) && (
          <div className="mb-3 flex items-end justify-between gap-3 px-1">
            <div className="min-w-0">
              {title && (
                <h3 className="truncate text-xl font-black tracking-tight text-gray-950 dark:text-white md:text-2xl">
                  {title}
                </h3>
              )}
              {subtitle && <p className="mt-0.5 truncate text-xs font-bold text-gray-500 dark:text-gray-400">{subtitle}</p>}
            </div>
            {onViewAll && (
              <button
                onClick={onViewAll}
                className="inline-flex shrink-0 items-center rounded-full bg-white/80 px-3 py-1.5 text-xs font-black text-primary-700 shadow-sm ring-1 ring-gray-200/70 transition-colors hover:bg-white dark:bg-white/[0.08] dark:text-primary-200 dark:ring-white/10 dark:hover:bg-white/[0.14] md:text-sm"
              >
                {t('common.view_all')} <ChevronRight className="w-4 h-4 rtl:-scale-x-100" />
              </button>
            )}
          </div>
        )}

        {/* Scroll Container - Spacer Strategy for Robust Mobile Padding */}
        <div
          ref={scrollRef}
          className="-mx-3 flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-3 pb-1 no-scrollbar md:-mx-1 md:gap-3 md:px-1"
        >
          {products.slice(0, 8).map((product, index) => (
            <div key={product.id} className="snap-start shrink-0">
              <div className="h-full w-[116px] sm:w-[132px] md:w-[200px]">
                <ProductCard
                  product={product}
                  index={index}
                  isCompact={true}
                  className="h-full border border-gray-100 dark:border-gray-800 shadow-sm"
                />
              </div>
            </div>
          ))}

          {/* View All Card at end of scroll */}
          {onViewAll && (
            <div className="snap-start shrink-0 flex items-center">
              <button
                onClick={onViewAll}
                className="flex min-h-[186px] w-[88px] flex-col items-center justify-center gap-2 rounded-3xl border border-gray-200/70 bg-white/80 text-sm font-black text-primary-700 shadow-sm ring-1 ring-white/70 transition-colors hover:border-primary-200 hover:bg-white dark:border-white/10 dark:bg-white/[0.06] dark:text-primary-200 dark:ring-white/10 dark:hover:border-primary-400/30 dark:hover:bg-white/[0.1] md:min-h-[254px] md:w-[108px]"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-sm dark:bg-white dark:text-gray-950">
                  <ArrowRight className="w-4 h-4" />
                </div>
                <span className="text-xs">{t('common.view_all')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const pickText = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const normalizeBannerSlide = (source = {}) => ({
  image: pickText(source, ['image', 'Image', 'imageUrl', 'imageURL', 'bannerImage', 'src', 'url']),
  title: pickText(source, ['title', 'Title', 'heading', 'headline']),
  subtitle: pickText(source, ['subtitle', 'Subtitle', 'description', 'Description', 'text']),
  badge: pickText(source, ['badge', 'Badge', 'label']),
  buttonText: pickText(source, ['buttonText', 'ButtonText', 'buttonLabel', 'ctaText', 'cta', 'button']),
  action: pickText(source, ['action', 'Action', 'link', 'href', 'route', 'target']),
  gradient: pickText(source, ['gradient', 'lightGradient', 'backgroundGradient', 'lightBackground', 'background', 'bannerGradient', 'heroGradient']),
  darkGradient: pickText(source, ['darkGradient', 'gradientDark', 'darkBackgroundGradient', 'darkBackground', 'bannerDarkGradient', 'heroDarkGradient']),
});

const hasBannerContent = (slide) => Boolean(
  slide?.image ||
  slide?.title ||
  slide?.subtitle ||
  slide?.badge ||
  slide?.buttonText ||
  slide?.action
);

const hasBannerGradient = (slide) => Boolean(slide?.gradient || slide?.darkGradient);

const normalizeBannerSlideList = (source) => {
  const list = Array.isArray(source)
    ? source
    : source && typeof source === 'object'
      ? Object.values(source)
      : [];

  return list
    .filter((item) => item && typeof item === 'object')
    .map(normalizeBannerSlide)
    .filter((slide) => hasBannerContent(slide) || hasBannerGradient(slide));
};

const parseBannerValue = (value) => {
  const result = {
    slides: [],
    htmlContent: '',
    staticLightGradient: '',
    staticDarkGradient: '',
  };

  if (!value) return result;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return result;

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return parseBannerValue(JSON.parse(trimmed));
      } catch {
        result.htmlContent = trimmed;
        return result;
      }
    }

    result.htmlContent = trimmed;
    return result;
  }

  if (Array.isArray(value)) {
    result.slides = normalizeBannerSlideList(value);
    return result;
  }

  if (typeof value !== 'object') return result;

  const nestedPayload =
    value.slides ??
    value.bannerSlides ??
    value.homeSlides ??
    value.heroSlides ??
    value.items ??
    value.banners ??
    value.banner ??
    value.homeBanner ??
    value.heroBanner ??
    null;

  if (nestedPayload) {
    const nested = parseBannerValue(nestedPayload);
    result.slides = nested.slides;
    result.htmlContent = nested.htmlContent;
    result.staticLightGradient = nested.staticLightGradient;
    result.staticDarkGradient = nested.staticDarkGradient;
  }

  if (typeof value.html === 'string' && value.html.trim()) {
    result.htmlContent = value.html.trim();
  }

  if (!result.slides.length) {
    const directSlide = normalizeBannerSlide(value);
    if (hasBannerContent(directSlide)) {
      result.slides = [directSlide];
    } else if (hasBannerGradient(directSlide)) {
      result.staticLightGradient = directSlide.gradient;
      result.staticDarkGradient = directSlide.darkGradient;
    } else {
      result.slides = normalizeBannerSlideList(value);
    }
  }

  return result;
};

const parseOrderTimestamp = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value > 1e11) return value;
    if (value > 1e9) return value * 1000;
    return 0;
  }
  if (value instanceof Date) {
    const ts = value.getTime();
    return Number.isFinite(ts) ? ts : 0;
  }

  const text = String(value).trim();
  if (!text) return 0;

  if (/^\d+$/.test(text)) {
    const numeric = Number(text);
    if (Number.isFinite(numeric)) {
      if (numeric > 1e11) return numeric;
      if (numeric > 1e9) return numeric * 1000;
    }
  }

  const parsedNative = Date.parse(text);
  if (Number.isFinite(parsedNative)) return parsedNative;

  const [datePart, timePart = '00:00:00'] = text.split(/[\sT]+/);
  const dParts = datePart.split(/[.:\-/]/).map((part) => parseInt(part, 10));
  const tParts = timePart.split(':').map((part) => parseInt(part, 10));

  if (dParts.length !== 3 || dParts.some((part) => !Number.isFinite(part))) return 0;

  let day;
  let month;
  let year;
  if (dParts[0] > 31) {
    year = dParts[0];
    month = dParts[1];
    day = dParts[2];
  } else {
    day = dParts[0];
    month = dParts[1];
    year = dParts[2];
  }

  const hours = Number.isFinite(tParts[0]) ? tParts[0] : 0;
  const minutes = Number.isFinite(tParts[1]) ? tParts[1] : 0;
  const seconds = Number.isFinite(tParts[2]) ? tParts[2] : 0;
  const parsed = new Date(year, (month || 1) - 1, day || 1, hours, minutes, seconds);
  const ts = parsed.getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const fetchRecentPendingOrders = async (uid, limit = 4) => {
  if (!uid) return [];
  try {
    const ordersQuery = query(
      ref(database, 'Order index'),
      orderByChild('Customer'),
      equalTo(uid),
      limitToLast(20)
    );
    const snapshot = await get(ordersQuery);
    if (!snapshot.exists()) return [];

    const pending = [];
    snapshot.forEach((child) => {
      const orderId = String(child.key || '').trim();
      const order = child.val() || {};
      const status = String(order.Status || order.status || '').toLowerCase();
      if (!orderId || status !== 'pending') return;

      const createdAt = parseOrderTimestamp(
        order['Order date'] ??
        order.orderDate ??
        order.createdAt ??
        order.orderedAt ??
        order.timestamp
      );

      pending.push({
        id: `pending_order_${orderId}`,
        type: 'order_pending',
        title: 'Order Pending',
        message: `Your order #${orderId} is currently pending review.`,
        severity: 'warning',
        createdAt,
        isRead: false,
        source: 'order',
        action: {
          label: 'View Order',
          route: '/orders'
        }
      });
    });

    return pending
      .sort((a, b) => {
        if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
        return String(b.id).localeCompare(String(a.id));
      })
      .slice(0, limit);
  } catch {
    return [];
  }
};

const Home = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, carouselImages, carouselLoaded, premium, exclusive, bundle, freeScripts, others, searchQuery, bannerHtml } = useSelector((state) => state.products);
  const trendingProducts = useSelector(selectTrendingProducts);
  const trendingLoading = useSelector(selectTrendingLoading);
  const trendingWindowDays = useSelector(selectTrendingWindowDays);
  const { coupons = [] } = useSelector((state) => state.cart || {});
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [selectedTrendingWindow, setSelectedTrendingWindow] = useState(7);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [showPointsTopUp, setShowPointsTopUp] = useState(false);
  const { settings: storeSettings } = useSelector((state) => state.settings);
  const topUpBonusPercent = Math.max(0, Number(storeSettings?.topUpBonusPercent) || 0);
  const popupQueueDoneEmittedRef = React.useRef(false);

  const emitPopupQueueDone = React.useCallback(() => {
    if (popupQueueDoneEmittedRef.current) return;
    popupQueueDoneEmittedRef.current = true;
    try {
      window.dispatchEvent(new CustomEvent('psh:popup-queue-done'));
    } catch {}
  }, []);

  // Popup Queue Logic
  const [currentPopupIndex, setCurrentPopupIndex] = useState(null);
  const bannerConfig = useMemo(() => parseBannerValue(bannerHtml), [bannerHtml]);
  const hasUsableBannerConfig = Boolean(
    bannerConfig.htmlContent ||
    bannerConfig.slides.length ||
    bannerConfig.staticLightGradient ||
    bannerConfig.staticDarkGradient
  );

  useEffect(() => {
    // Only fetch if we don't have data to avoid reload flash on back navigation
    if (!premium?.length && !exclusive?.length && !bundle?.length && !freeScripts?.length && !others?.length) {
      dispatch(fetchProducts());
    }
    if (!carouselImages?.length) {
      dispatch(fetchCarouselImages());
    }
    // Always try to update trending/banner as they might change, but maybe silently?
    // For now, let's keep them conditional to ensure smooth 'back'
    if (!trendingProducts?.length) {
      dispatch(fetchTrendingProducts(selectedTrendingWindow));
    }
    if (!hasUsableBannerConfig) {
      dispatch(fetchBannerHtml());
    }
    dispatch(fetchStoreDiscounts());
    if (!coupons?.length) {
      dispatch(fetchCoupons());
    }
  }, [dispatch, premium?.length, exclusive?.length, bundle?.length, freeScripts?.length, others?.length, carouselImages?.length, trendingProducts?.length, hasUsableBannerConfig, coupons?.length, selectedTrendingWindow]);

  useEffect(() => {
    if (selectedTrendingWindow === trendingWindowDays) return;
    dispatch(fetchTrendingProducts(selectedTrendingWindow));
  }, [dispatch, selectedTrendingWindow, trendingWindowDays]);

  useEffect(() => {
    const loadRecent = async () => {
      const uid = String(user?.uid || '').trim();
      if (!uid) {
        setRecentNotifications([]);
        return;
      }
      try {
        const [list, pendingOrders] = await Promise.all([
          fetchRecentNotifications(uid, 8),
          fetchRecentPendingOrders(uid, 4),
        ]);

        const filteredNotifications = list
          .filter((item) => {
            const type = String(item?.type || '').toLowerCase();
            const isPendingType = type.includes('order_pending');
            if (item?.isRead && !isPendingType) return false;
            return (
              isPendingType ||
              type.includes('order_approved') ||
              type.includes('order_cancelled') ||
              type.includes('order_refunded') ||
              type.includes('order_rejected') ||
              type.includes('license_expiring_soon') ||
              type.includes('license_expired')
            );
          })
          .map((item) => ({ ...item, source: 'notification' }));

        const merged = [...filteredNotifications, ...pendingOrders]
          .sort((a, b) => {
            if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
            return String(b.id).localeCompare(String(a.id));
          })
          .slice(0, 4);

        setRecentNotifications(merged);
      } catch {
        setRecentNotifications([]);
      }
    };
    loadRecent();
  }, [user?.uid]);

  useEffect(() => {
    if (!carouselLoaded) return;

    let hasOpened = false;
    try { hasOpened = sessionStorage.getItem('psh-store-popup-opened'); } catch (e) { }

    // Start queue if not opened yet and we have data
    if (!hasOpened && Array.isArray(carouselImages) && carouselImages.length > 0) {
      setCurrentPopupIndex(0);
      return;
    }

    setCurrentPopupIndex(null);
    emitPopupQueueDone();
  }, [carouselImages, carouselLoaded, emitPopupQueueDone]);

  const closeCurrentPopup = () => {
    if (Array.isArray(carouselImages) && currentPopupIndex !== null) {
      // If there are more popups in the queue
      if (currentPopupIndex < carouselImages.length - 1) {
        setCurrentPopupIndex(prev => prev + 1);
      } else {
        // End of queue
        setCurrentPopupIndex(null);
        try { sessionStorage.setItem('psh-store-popup-opened', 'true'); } catch (e) { }
        emitPopupQueueDone();
      }
    } else {
      setCurrentPopupIndex(null);
      emitPopupQueueDone();
    }
  };

  const goTo = (category) => {
    dispatch(setSearchQuery(''));
    if (category === 'all') navigate('/products');
    else navigate(`/products/${category}`);
  };

  const isProductActive = (product) => {
    const status = String(product?.status ?? '').trim().toLowerCase();
    return !status || status === 'active';
  };

  const activePremium = useMemo(() => (premium || []).filter(isProductActive), [premium]);
  const activeExclusive = useMemo(() => (exclusive || []).filter(isProductActive), [exclusive]);
  const activeBundle = useMemo(() => (bundle || []).filter(isProductActive), [bundle]);
  const activeFreeScripts = useMemo(() => (freeScripts || []).filter(isProductActive), [freeScripts]);
  const activeOthers = useMemo(() => (others || []).filter(isProductActive), [others]);
  const activeTrendingProducts = useMemo(() => (trendingProducts || []).filter(isProductActive), [trendingProducts]);
  const activeCoupons = useMemo(() => (
    coupons || []
  ).filter(c => (
    (!c.status || c.status.toLowerCase() === 'active') &&
    (!c.endDate || new Date(c.endDate) > new Date()) &&
    isCouponVisibleToUser(c, user)
  )), [coupons, user]);



  if (loading && !searchQuery) return <PageLoader />;

  // Quick Categories Data
  const quickCategories = [
    { label: t('home.premium_label'), icon: StarIcon, color: 'bg-gradient-to-br from-yellow-400 to-yellow-600', action: () => goTo('premium') },
    { label: t('home.bundle_label'), icon: Package, color: 'bg-gradient-to-br from-purple-500 to-indigo-600', action: () => goTo('bundle') },
    { label: t('home.free_script_label', 'Free Scripts'), icon: Globe, color: 'bg-gradient-to-br from-emerald-500 to-teal-600', action: () => goTo('free') },
    {
      label: t('home.trending_label'), icon: TrendingUp, color: 'bg-gradient-to-br from-red-500 to-pink-600', action: () => {
        document.getElementById('trending')?.scrollIntoView({ behavior: 'smooth' });
      }
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-gray-50 pb-0 text-gray-950 dark:bg-gray-950 dark:text-white">
      <div className="relative mx-auto w-full max-w-6xl px-3 pb-8 pt-3 md:px-6 md:pb-12 md:pt-5">
        <section className="mb-3 md:mb-4">
          {bannerConfig.htmlContent ? (
            <div className="overflow-hidden rounded-2xl" dangerouslySetInnerHTML={{ __html: sanitizeHtmlContent(bannerConfig.htmlContent) }} />
          ) : bannerConfig.slides.length > 0 ? (
            <BannerCarousel slides={bannerConfig.slides} />
          ) : (
            <SeasonalCampaignHero />
          )}
        </section>

        {recentNotifications.length > 0 && (
          <section className="mb-3 md:mb-4">
            <div className="mb-1.5 flex items-center gap-1.5 px-0.5">
              <Bell className="h-3.5 w-3.5 text-primary-600 dark:text-primary-300" />
              <span className="text-[11px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('home.recent_notifications', 'Recent Notifications')}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {recentNotifications.map((item) => (
                <button
                  key={item.id}
                  onClick={async () => {
                    const uid = String(user?.uid || '').trim();
                    const type = String(item?.type || '').toLowerCase();
                    const isPendingType = type.includes('order_pending');
                    if (uid && item.id && item.source === 'notification' && !item.isRead && !isPendingType) {
                      try { await markNotificationRead(uid, item.id); } catch {}
                    }
                    if (!isPendingType) {
                      setRecentNotifications((prev) => prev.filter((row) => row.id !== item.id));
                    }
                    navigate(getNotificationRoute(item));
                  }}
                  className="group flex min-w-0 items-center gap-2 rounded-xl border border-gray-200/70 bg-white/[0.78] px-2.5 py-1.5 text-left transition-colors hover:border-primary-200 hover:bg-white dark:border-white/10 dark:bg-white/[0.055] dark:hover:border-primary-400/30 dark:hover:bg-white/[0.09]"
                >
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                  <span className="min-w-0 flex-1 truncate text-xs font-black text-gray-950 dark:text-white">
                    {item.title}
                    <span className="ml-1.5 font-medium text-gray-500 dark:text-gray-400">{item.message}</span>
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="border-y border-gray-200/70 py-3 dark:border-white/10 md:py-4">
          <div className="grid grid-cols-4 gap-1">
            {quickCategories.map((cat) => (
              <QuickCategoryItem key={cat.label} {...cat} onClick={cat.action} />
            ))}
          </div>
        </section>

        {isAuthenticated && (
          <section className="py-3 md:py-4">
            <button
              onClick={() => setShowPointsTopUp(true)}
              className="group flex w-full items-center gap-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-white p-3 text-left shadow-sm transition-colors hover:border-amber-300 hover:from-amber-100 dark:border-amber-500/20 dark:from-amber-500/10 dark:via-orange-500/10 dark:to-transparent dark:hover:border-amber-400/40 md:p-4"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-500 text-white shadow-lg shadow-amber-500/25 md:h-12 md:w-12">
                <Coins className="h-5 w-5 md:h-6 md:w-6" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-black text-gray-950 dark:text-white md:text-base">
                    {t('home.topup_title', 'Top Up Reward Points')}
                  </span>
                  {topUpBonusPercent > 0 && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-white">
                      <Sparkles className="h-3 w-3" />
                      +{topUpBonusPercent}%
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-xs font-bold text-gray-500 dark:text-gray-400">
                  {topUpBonusPercent > 0
                    ? t('home.topup_subtitle_bonus', `Buy points and get ${topUpBonusPercent}% extra`)
                    : t('home.topup_subtitle', 'Buy reward points and save on every order')}
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-amber-600 transition-transform group-hover:translate-x-0.5 dark:text-amber-400" />
            </button>
          </section>
        )}

        <div className="space-y-0">
          <section className="border-b border-gray-200/70 py-4 dark:border-white/10 md:py-5" id="trending">
            <div className="mb-3 flex items-end justify-between gap-3 px-1">
              <div className="min-w-0">
                <h3 className="truncate text-xl font-black tracking-tight text-gray-950 dark:text-white md:text-2xl">
                  {t('home.trending_title')}
                </h3>
                <p className="mt-0.5 truncate text-xs font-bold text-gray-500 dark:text-gray-400">{t('home.trending_subtitle')}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1 rounded-full border border-gray-200/70 bg-white/80 p-1 shadow-sm dark:border-white/10 dark:bg-white/[0.08]">
                {[7, 15, 30, 0].map((days) => (
                  <button
                    key={days}
                    onClick={() => setSelectedTrendingWindow(days)}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-black transition-colors ${
                      selectedTrendingWindow === days
                        ? 'bg-gray-950 text-white shadow-sm dark:bg-white dark:text-gray-950'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {days === 0 ? 'ALL' : `${days}D`}
                  </button>
                ))}
              </div>
            </div>
            <ProductSwimlane
              title=""
              subtitle=""
              products={activeTrendingProducts}
              loading={trendingLoading}
              showTopBorder={false}
            />
          </section>

          {activeCoupons.length > 0 && (
            <section className="border-b border-gray-200/70 py-4 dark:border-white/10 md:py-5">
              <div className="mb-3 flex items-end justify-between gap-3 px-1">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-gray-950 text-white shadow-sm dark:bg-white dark:text-gray-950">
                    <BadgePercent className="h-[18px] w-[18px]" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="truncate text-xl font-black tracking-tight text-gray-950 dark:text-white md:text-2xl">
                      {t('home.latest_offers', 'Latest Offers')}
                    </h3>
                    <p className="mt-0.5 truncate text-xs font-bold text-gray-500 dark:text-gray-400">
                      {t('home.latest_offers_subtitle', 'Unlock exclusive discounts today')}
                    </p>
                  </div>
                </div>
                <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200">
                  {activeCoupons.length} live
                </span>
              </div>
              <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-1 no-scrollbar">
                {activeCoupons.slice(0, 5).map(coupon => (
                  <div key={coupon.code} className="w-[245px] shrink-0 snap-start sm:w-[280px]">
                    <CouponCard coupon={coupon} isCompact={true} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeExclusive.length > 0 && (
            <ProductSwimlane
              title={t('home.exclusive_title', 'Exclusive')}
              subtitle={t('home.exclusive_subtitle', 'Premium-only releases and VIP drops')}
              products={activeExclusive}
              onViewAll={() => goTo('exclusive')}
            />
          )}

          <ProductSwimlane
            title={t('home.premium_title')}
            subtitle={t('home.premium_subtitle')}
            products={activePremium}
            onViewAll={() => goTo('premium')}
          />

          <ProductSwimlane
            title={t('home.bundle_title')}
            subtitle={t('home.bundle_subtitle')}
            products={activeBundle}
            onViewAll={() => goTo('bundle')}
          />

          {activeFreeScripts.length > 0 && (
            <ProductSwimlane
              title={t('home.free_script_title', 'Free Scripts')}
              subtitle={t('home.free_script_subtitle', 'Download directly without checkout')}
              products={activeFreeScripts}
              onViewAll={() => goTo('free')}
            />
          )}

          {activeOthers.length > 0 && (
            <ProductSwimlane
              title={t('home.more_title')}
              products={activeOthers}
              onViewAll={() => goTo('other')}
            />
          )}
        </div>
      </div>

      <PopupModal
        isOpen={currentPopupIndex !== null}
        onClose={closeCurrentPopup}
        content={
          (Array.isArray(carouselImages) && currentPopupIndex !== null)
            ? carouselImages[currentPopupIndex]
            : null
        }
      />

      <PointsTopUpModal
        isOpen={showPointsTopUp}
        onClose={() => setShowPointsTopUp(false)}
      />
    </div>
  );
};

export default Home;
